import React from 'react';
import { Box, Grid, LinearProgress, Paper, Typography } from '@mui/material';
import type { UsageSessionRow } from '../types';
import { formatPercent, shareSumText } from '../services/usageFormat';
import { SCHUTZ_SAMMELZEILE, UNBEKANNT } from './SchutzZeile';

/**
 * RITA-1505 (Nutzungserfassung Phase 2c) — Geräte und Darstellung als Balkenlisten.
 *
 * Bewusst ohne Diagramm-Bibliothek: fünf Anteilslisten sind mit einem Fortschrittsbalken je Zeile
 * vollständig darstellbar, und eine neue Abhängigkeit für Balken wäre nicht verhältnismäßig.
 *
 * Der Nenner von `share` enthält auch Ausprägungen jenseits der ausgegebenen 100. Summieren sich
 * die Anteile nicht auf 100 %, steht das unter der Liste — sonst sähe die Kappung wie ein
 * Rechenfehler aus.
 */
export interface AnteilsListeProps {
  titel: string;
  rows: UsageSessionRow[];
}

/** Beschriftung einer Zeile: Sammelzeilen nennen die Zahl der Gruppen, nie eine Ausprägung. */
export const anteilsBezeichner = (row: UsageSessionRow): string => {
  if (row.suppressed) return `${SCHUTZ_SAMMELZEILE} (${row.groups})`;
  return row.value === null || row.value === '' ? UNBEKANNT : row.value;
};

export function AnteilsListe({ titel, rows }: AnteilsListeProps) {
  const hinweis = shareSumText(rows);
  return (
    <Paper variant="outlined" data-testid="nutzung-anteilsliste" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
        {titel}
      </Typography>
      {rows.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Keine Sitzungen im Zeitraum.
        </Typography>
      )}
      {rows.map((row, stelle) => (
        // eslint-disable-next-line react/no-array-index-key
        <Box key={`${row.label}-${stelle}`} sx={{ mt: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{ color: row.suppressed ? 'text.disabled' : 'text.primary', wordBreak: 'break-word' }}
            >
              {anteilsBezeichner(row)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatPercent(row.share)}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            // share ist ein Anteil 0..1; der Balken erwartet Prozent und wird bei 100 gekappt.
            value={Math.min(100, Math.max(0, (row.share ?? 0) * 100))}
            sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
          />
        </Box>
      ))}
      {hinweis && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          {hinweis}
        </Typography>
      )}
    </Paper>
  );
}

export interface AnteilsListenProps {
  device: UsageSessionRow[];
  browser: UsageSessionRow[];
  theme: UsageSessionRow[];
  layout: UsageSessionRow[];
  viewport: UsageSessionRow[];
}

export default function AnteilsListen({ device, browser, theme, layout, viewport }: AnteilsListenProps) {
  const listen: AnteilsListeProps[] = [
    { titel: 'Geräteklasse', rows: device },
    { titel: 'Browser', rows: browser },
    { titel: 'Hell / Dunkel', rows: theme },
    { titel: 'Layout', rows: layout },
    { titel: 'Fensterbreite', rows: viewport },
  ];
  return (
    <Grid container spacing={2}>
      {listen.map(({ titel, rows }) => (
        <Grid item xs={12} sm={6} md={4} key={titel}>
          <AnteilsListe titel={titel} rows={rows} />
        </Grid>
      ))}
    </Grid>
  );
}
